import Image from 'next/image';
import { useState } from 'react';

import { MdGroups, MdReplay, MdContentCopy, MdCheck } from 'react-icons/md';

import { Button, Typography } from 'darrius';

import { imgPrefix } from '@/utils/imgPrefix';

import { showToast, Toast } from '@/components/atoms/Toast/Toast';
import { CreditCardErrorModal } from '@/components/atoms';

import {
  purchaseClubWithCard,
  generateClubPix,
  CLUB_PRICE_IN_CENTS,
  CLUB_PRODUCT,
} from '@/services/purchases/club';

import { type IClubTemplateProps } from './@interfaces';

import styles from './ClubTemplate.module.scss';

const BENEFITS = [
  {
    icon: MdGroups,
    title: '20 leads por mês',
    description:
      'Receba 20 pedidos de clientes todos os meses para fechar mais negócios direto no seu email.',
  },
  {
    icon: MdReplay,
    title: 'Até 3 reembolsos',
    description:
      'Comprou um pedido e o cliente não respondeu? Você tem até 3 reembolsos de moedas por mês, sem dor de cabeça.',
  },
];

const PRICE_LABEL = (CLUB_PRICE_IN_CENTS / 100).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

function ClubTemplate({
  userInfo,
  paymentCard,
  origin = 'clube',
}: IClubTemplateProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [showCompletedPurchase, setShowCompletedPurchase] = useState(false);
  const [pixCode, setPixCode] = useState('');
  const [copied, setCopied] = useState(false);

  async function handleSubscribe() {
    if (isLoading || !paymentCard) {
      return;
    }

    setIsLoading(true);

    try {
      const data = await purchaseClubWithCard({
        card: paymentCard,
        autoRenew,
        userInfo,
      });

      showToast({
        message: 'Pagamento realizado com sucesso',
        type: 'success',
      });

      setShowCompletedPurchase(true);

      if (window?.newrelic) {
        window.newrelic.addPageAction('club_action', {
          step: 'creditcard_payment_success',
          origin,
          invoice_id: data?.invoice_id,
          type: 'saved_card',
          profileId: userInfo?.profile?.id,
        });
      }
    } catch (error) {
      // Cartão recusado: abre o mesmo modal do checkout de moedas, que leva
      // o profissional para o Pix.
      setShowFallbackModal(true);

      if (window?.newrelic) {
        window.newrelic.noticeError(error, {
          step: 'creditcard_payment_error',
          origin,
          status: error?.response?.status,
          message: error?.response?.data?.clientMessage || error?.message,
          type: 'saved_card',
          bundle_id: CLUB_PRODUCT.nameId,
          profileId: userInfo?.profile?.id,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleGeneratePix() {
    setShowFallbackModal(false);
    setIsLoading(true);

    try {
      const { pixCode: code } = await generateClubPix({ userInfo });

      setPixCode(code);

      if (window?.newrelic) {
        window.newrelic.addPageAction('club_action', {
          step: 'pix_generate_code_success',
          origin,
          profileId: userInfo?.profile?.id,
        });
      }
    } catch (error) {
      if (window?.newrelic) {
        window.newrelic.addPageAction('club_action', {
          step: 'pix_generate_code_error',
          origin,
          profileId: userInfo?.profile?.id,
        });
      }

      showToast({
        message:
          error?.response?.data?.clientMessage ||
          'Não foi possível gerar o Pix',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCopyPix() {
    if (!pixCode) {
      return;
    }

    try {
      await navigator.clipboard.writeText(pixCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  }

  if (showCompletedPurchase) {
    return (
      <main className={styles.clube}>
        <section className={styles.completed}>
          <img
            width="140px"
            height="140px"
            src={imgPrefix('/images/completed-purchase.svg')}
            alt="Ilustração de compra concluída"
          />

          <Typography.Subtitle size="l">
            Assinatura confirmada!
          </Typography.Subtitle>

          <Typography.Text className={styles.completed__text}>
            Bem-vindo ao Clube de Benefícios GetNinjas. Seus leads serão
            disponibilizados de 10 em 10 ao longo do mês, a partir da próxima
            sexta-feira. Você já pode sair desta tela.
          </Typography.Text>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.clube}>
      <section className={styles.hero}>
        <Image
          className={styles.hero__logo}
          src={imgPrefix('/images/logo-getninjas.svg')}
          alt="GetNinjas"
          width={136}
          height={28}
          quality={100}
          priority
        />

        <Image
          className={styles.hero__illustration}
          src={imgPrefix('/images/pro-illustration.svg')}
          alt="Profissional do GetNinjas"
          width={220}
          height={220}
          quality={100}
          priority
        />

        <Typography.Title as="h1" size="2xl" className={styles.hero__title}>
          Clube de Benefícios GetNinjas
        </Typography.Title>

        <Typography.Text className={styles.hero__subtitle}>
          Mais pedidos, mais tranquilidade e mais negócios fechados todo mês.
        </Typography.Text>
      </section>

      <section className={styles.benefits}>
        {BENEFITS.map(({ icon: Icon, title, description }) => (
          <article key={title} className={styles.card}>
            <span className={styles.card__icon}>
              <Icon size={32} />
            </span>

            <Typography.Subtitle
              as="h2"
              size="m"
              className={styles.card__title}
            >
              {title}
            </Typography.Subtitle>

            <Typography.Text className={styles.card__description}>
              {description}
            </Typography.Text>
          </article>
        ))}
      </section>

      <section className={styles.checkout}>
        {pixCode && (
          <div className={styles.pix}>
            <Typography.Text className={styles.pix__label}>
              Pague o Pix abaixo para ativar sua assinatura:
            </Typography.Text>

            <button
              type="button"
              className={styles.pix__code}
              onClick={handleCopyPix}
            >
              <span className={styles.pix__value}>{pixCode}</span>
              {copied ? <MdCheck size={20} /> : <MdContentCopy size={20} />}
            </button>

            <Typography.Text className={styles.pix__hint}>
              {copied ? 'Código copiado!' : 'Toque para copiar o código'}
            </Typography.Text>
          </div>
        )}

        <Button.Root
          type="button"
          fullWidth
          onClick={handleSubscribe}
          isLoading={isLoading}
          disabled={isLoading || !paymentCard}
        >
          Assinar por {PRICE_LABEL}/mês
        </Button.Root>

        <p className={styles.checkout__authorization}>
          Ao clicar em comprar autorizo a GetNinjas a utilizar o meu último
          método de pagamento válido.
        </p>

        <label className={styles.checkout__renew}>
          <input
            type="checkbox"
            checked={autoRenew}
            onChange={(event) => setAutoRenew(event.target.checked)}
          />
          <span>Renovação automática da assinatura</span>
        </label>
      </section>

      <CreditCardErrorModal
        isOpen={showFallbackModal}
        onOpenChange={setShowFallbackModal}
        onRedirectToPixInstallments={handleGeneratePix}
        description="Não conseguimos concluir o pagamento com o seu cartão, mas você ainda pode assinar o clube. Pague com Pix — sem cartão, de forma rápida e segura."
        confirmLabel="Pagar com Pix"
      />

      <Toast />
    </main>
  );
}

export default ClubTemplate;
